import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem, Modal, Select, TextField } from '@mui/material'
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react'
import ConfirmModal from './confirmModal';
import { mixedNaturalSort } from '../utils/generalFunctions';
import { LocalizationProvider, TimeField } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const MultiSchedulesModal = ({ onOpen, onClose, onSave, devices, userSchedules, routeOrder, multiScheduleCount, setMultiScheduleCount, scheduleName, setScheduleName, controlPointStart, controlPointEnd, startTime, showAlert, isEditing = false }) => {

    const [multiScheduleInterval, setMultiScheduleInterval] = useState({ minutes: 5, seconds: 0 });
    const [multiSchedules, setMultiSchedules] = useState([]);
    const [errorFields, setErrorFields] = useState([]);

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingMultiScheduleChange, setPendingMultiScheduleChange] = useState(null);

    const [newScheduleName, setNewScheduleName] = useState(scheduleName || "Horario");

    useEffect(() => {
        setNewScheduleName(scheduleName || "Horario");
    }, [scheduleName]);

    const validateNumberInput = (setter) => (value, min = 1, max) => {
        const newValue = Math.min(Math.max(min, Number(value)), max);
        setter(newValue);
    };

    useEffect(() => {
        if (onOpen) {
            initializeMultiSchedules(multiScheduleCount);
        }
    }, [onOpen]);

    const intervalInSeconds = multiScheduleInterval.minutes * 60 + multiScheduleInterval.seconds;

    const [previousIntervalInSeconds, setPreviousIntervalInSeconds] = useState(intervalInSeconds);

    useEffect(() => {
        if (multiSchedules.length === 0) return;

        let baseTime;

        if (isEditing) {
            // Diferencia entre el intervalo nuevo y el anterior
            const diffSeconds = intervalInSeconds - previousIntervalInSeconds;

            // Mover también el primer horario
            baseTime = dayjs(multiSchedules[0].startTime, 'HH:mm:ss')
                .add(diffSeconds, 'second');
        } else {
            baseTime = dayjs(startTime, 'HH:mm:ss');
        }

        const updatedSchedules = multiSchedules.map((schedule, index) => {
            const newStartTime = baseTime
                .add(index * intervalInSeconds, 'second')
                .format('HH:mm:ss');

            return {
                ...schedule,
                startTime: newStartTime,
            };
        });

        setPreviousIntervalInSeconds(intervalInSeconds);
        setMultiSchedules(updatedSchedules);

    }, [multiScheduleInterval]);

    useEffect(() => {
        if (multiScheduleCount > multiSchedules.length) {
            addNewSchedules(multiScheduleCount - multiSchedules.length);
        } else if (multiScheduleCount < multiSchedules.length) {
            removeLastSchedules(multiSchedules.length - multiScheduleCount);
        }
    }, [multiScheduleCount]);

    const initializeMultiSchedules = (multiScheduleCount = 2) => {
        const schedules = [];
        const baseName = newScheduleName;

        // Crear un Set con los nombres existentes
        const existingNames = new Set([
            ...userSchedules.map(schedule => schedule.name),
        ]);

        let position = 1; // Siempre comenzar desde la primera posición disponible
        for (let i = 0; i < multiScheduleCount; i++) {
            // Buscar el siguiente nombre único disponible
            while (existingNames.has(`${baseName} ${position}`)) {
                position++;
            }

            schedules.push({
                name: `${baseName} ${position}`,
                vehicleId: null,
                startTime: dayjs(startTime).add(i * multiScheduleInterval.minutes * 60 + multiScheduleInterval.seconds, 'second').format('HH:mm:ss'),
            });

            position++; // Incrementar para el siguiente nombre
        }

        setMultiSchedules(schedules);
        setErrorFields([]);
    };

    const addNewSchedules = (count) => {
        const baseName = newScheduleName;
        const updatedSchedules = [...multiSchedules];

        // Crear un Set con los nombres existentes
        const existingNames = new Set([
            ...updatedSchedules.map(schedule => schedule.name),
            ...userSchedules.map(schedule => schedule.name),
        ]);

        let position = 2;
        // Busca el próximo número libre para el nombre
        while (existingNames.has(`${baseName} ${position}`)) {
            position++;
        }

        // Hora base: del último horario actual, o startTime si no hay
        let lastTime = updatedSchedules.length > 0
            ? updatedSchedules[updatedSchedules.length - 1].startTime
            : dayjs(startTime).format('HH:mm:ss');

        for (let i = 0; i < count; i++) {
            // Calcula el nuevo tiempo a partir del anterior
            const newTime = dayjs(lastTime, 'HH:mm:ss')
                .add(multiScheduleInterval.minutes * 60 + multiScheduleInterval.seconds, 'second')
                .format('HH:mm:ss');

            // Busca nombre único
            while (existingNames.has(`${baseName} ${position}`)) {
                position++;
            }

            updatedSchedules.push({
                name: `${baseName} ${position}`,
                vehicleId: null,
                startTime: newTime,
            });

            existingNames.add(`${baseName} ${position}`);
            lastTime = newTime;
            position++;
        }

        setMultiSchedules(updatedSchedules);
    };



    const removeLastSchedules = (count) => {
        setMultiSchedules(multiSchedules.slice(0, -count));
    };

    const validateSchedules = () => {
        const names = multiSchedules.map(schedule => schedule.name);
        const existingNames = new Set(userSchedules.map(schedule => schedule.name));

        const newErrorFields = names.reduce((acc, name, i) => {
            if (name.trim() !== "" && (names.indexOf(name) !== i || existingNames.has(name))) {
                acc.push(i);
            }
            return acc;
        }, []);
        setErrorFields(newErrorFields);
    };

    const handleMultiScheduleChange = (index, field, value) => {
        const updatedSchedules = [...multiSchedules];

        if (field === 'vehicle') {
            const device = devices.find(device => device.label === value);
            const newVehicleId = device ? device.id : null;

            // Verificar si el vehicleId ya está asignado en multiSchedules o userSchedules
            const isAssignedInMultiSchedules = updatedSchedules.some(
                (schedule, idx) => schedule.vehicleId === newVehicleId && idx !== index
            );

            const isAssignedInUserSchedules = userSchedules.some(
                schedule => schedule.vehicleId === newVehicleId
            );

            if (newVehicleId && (isAssignedInMultiSchedules || isAssignedInUserSchedules)) {
                // Guardar el cambio pendiente y mostrar el diálogo de confirmación
                setPendingMultiScheduleChange({ index, newVehicleId });
                setIsConfirmModalOpen(true);
                return;
            }

            updatedSchedules[index].vehicleId = newVehicleId;
        } else {
            updatedSchedules[index][field] = value;
        }

        if (field === 'name') {
            const names = updatedSchedules.map(schedule => schedule.name);
            const existingNames = new Set(userSchedules.map(schedule => schedule.name));

            // Actualizar nombres secuenciales si cambia el primero
            if (index === 0) {
                setNewScheduleName(value); // Actualizar el estado del nuevo nombre base
                const regex = /(.*?)(\s*)(\d+)$/; // Captura texto antes y el número al final
                const match = value.match(regex);
                if (match) {
                    const baseName = match[1].trim();
                    const spaces = match[2];
                    let startNumber = parseInt(match[3], 10);

                    updatedSchedules[0].name = value; // Actualizar el primer nombre

                    // Actualizar nombres de los horarios restantes secuencialmente
                    for (let i = 1; i < updatedSchedules.length; i++) {
                        updatedSchedules[i].name = `${baseName}${spaces}${startNumber + i}`;
                    }
                } else {
                    // Si no hay número al final, mantener la lógica actual
                    updatedSchedules[0].name = value;
                    for (let i = 1; i < updatedSchedules.length; i++) {
                        updatedSchedules[i].name = `${value} ${i + 1}`;
                    }
                }
            }

            const newErrorFields = names.reduce((acc, name, i) => {
                if (name.trim() !== "" && (names.indexOf(name) !== i || existingNames.has(name))) {
                    acc.push(i);
                }
                return acc;
            }, []);

            setErrorFields(newErrorFields); // Highlight duplicate fields
        }
        // console.log(updatedSchedules);
        setMultiSchedules(updatedSchedules);
        validateSchedules();
    };

    const applyVehicleChange = (index, newVehicleId) => {
        const updatedSchedules = [...multiSchedules];

        // Desasignar vehículo del horario anterior en multiSchedules
        const previousIndex = updatedSchedules.findIndex(
            (schedule, idx) => schedule.vehicleId === newVehicleId && idx !== index
        );

        if (previousIndex !== -1) {
            updatedSchedules[previousIndex].vehicleId = null;
        }

        // Asignar vehículo al nuevo horario
        updatedSchedules[index].vehicleId = newVehicleId;

        setMultiSchedules(updatedSchedules);
        validateSchedules();
    };

    const handleAcceptMultiSchedule = () => {
        if (errorFields.length > 0) {
            // alert("Corrija los errores antes de aceptar.");
            showAlert("Corrija los errores antes de aceptar.", "warning");
            return;
        }

        onSave(multiSchedules);
        onClose();
    };

    const handleClose = () => {
        setMultiSchedules([]);
        onClose();
    };

    return (
        <>
            <ConfirmModal title={'Confirmación'} text={`Este vehículo ya se encuentra asignado al horario "${userSchedules.find((schedule) => schedule.vehicleId === pendingMultiScheduleChange?.newVehicleId)?.name || ''}". ¿Desea asignarlo a este horario de cualquier manera?`} isOpen={isConfirmModalOpen} onClose={setIsConfirmModalOpen}
                confirmFunction={() => {
                    if (pendingMultiScheduleChange) {
                        applyVehicleChange(
                            pendingMultiScheduleChange.index,
                            pendingMultiScheduleChange.newVehicleId
                        );
                    }
                    setIsConfirmModalOpen(false);
                }} />
            <Modal open={onOpen} onClose={handleClose}>
                <Box className="modal_box multi_schedules_modal">
                    <h1 style={{ textAlign: "center" }}>Generar varios horarios</h1>
                    <div className="form_container">
                        <TextField
                            fullWidth
                            type="number"
                            label="Cantidad de Horarios"
                            variant="outlined"
                            value={multiScheduleCount}
                            onChange={(e) => validateNumberInput(setMultiScheduleCount)(e.target.value, 2, 50)}
                            slotProps={{
                                input: {
                                    min: 2,
                                    max: 50,
                                    step: 1
                                },
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root.Mui-error": {
                                    borderColor: "red",
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                                    },
                                    '&:not(.Mui-focused):hover fieldset': {
                                        borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '0.8rem', // Tamaño del texto dentro del campo de entrada
                                    padding: '1rem 12px', // Ajuste de padding para alinear correctamente el texto
                                    color: 'var(--color-dark)', // Color del texto
                                }
                            }}
                        />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimeField
                                fullWidth
                                label="Intervalo entre Horarios (mm:ss)"
                                variant="outlined"
                                format="mm:ss"
                                value={dayjs().hour(0).minute(multiScheduleInterval?.minutes ?? 5).second(multiScheduleInterval?.seconds ?? 0)}
                                onChange={(newValue) => {
                                    if (!newValue) return;
                                    let minutes = newValue.minute();
                                    let seconds = newValue.second();
                                    let totalSeconds = minutes * 60 + seconds;
                                    // Limita a 30 minutos máximo (30:00 == 1800 segundos)
                                    if (totalSeconds < 60) {
                                        minutes = 1;
                                        seconds = 0;
                                        totalSeconds = 60;
                                    } else if (totalSeconds > 1800) {
                                        minutes = 30;
                                        seconds = 0;
                                        totalSeconds = 1800;
                                    }
                                    setMultiScheduleInterval({ minutes, seconds });
                                }}

                                sx={{
                                    '& .MuiInputLabel-root': {
                                        color: 'var(--color-dark)', // Cambia el color de la label
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                                        },
                                        '&:not(.Mui-focused):hover fieldset': {
                                            borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '0.8rem', // Tamaño del texto dentro del campo de entrada
                                        padding: '1rem 12px', // Ajuste de padding para alinear correctamente el texto
                                        color: 'var(--color-dark)', // Color del texto
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
                        <h2 style={{ marginBottom: "0.5rem" }}>Detalles</h2>
                        <div className="multi_schedule_container">
                            {multiSchedules.map((schedule, index) => (
                                <div key={index} className="multi_schedule_item">
                                    <TextField
                                        fullWidth
                                        label={`Nombre del Horario ${index + 1}`}
                                        variant="outlined"
                                        value={schedule.name}
                                        onChange={(e) => handleMultiScheduleChange(index, "name", e.target.value)}
                                        error={errorFields.includes(index)}
                                        helperText={errorFields.includes(index) ? "Nombre duplicado" : ""}
                                        sx={{
                                            '& .MuiInputLabel-root': {
                                                color: 'var(--color-dark)', // Cambia el color de la label
                                                fontSize: '0.7rem',
                                            },
                                            '& .MuiInputLabel-shrink': {
                                                fontSize: '1rem', // Tamaño de la label cuando está en foco
                                            },
                                            "& .MuiOutlinedInput-root.Mui-error": {
                                                borderColor: "red",
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                                                },
                                                '&:not(.Mui-focused):hover fieldset': {
                                                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.7rem', // Tamaño del texto dentro del campo de entrada
                                                padding: '1rem 12px', // Ajuste de padding para alinear correctamente el texto
                                                color: 'var(--color-dark)', // Color del texto
                                            }
                                        }}
                                    />
                                    <FormControl fullWidth >
                                        <InputLabel id={`vehicle-select-label-${index}`}>Asignar vehículo</InputLabel>
                                        <Select
                                            labelId={`vehicle-select-label-${index}`}
                                            value={schedule.vehicleId ? devices.find(device => device.id === schedule.vehicleId)?.label : "Ninguno"}
                                            onChange={(e) => handleMultiScheduleChange(index, "vehicle", e.target.value)}
                                            label="Asignar vehículo"
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                                                },
                                                '&:not(.Mui-focused):hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                                                },
                                                '& .MuiSelect-icon': {
                                                    color: 'var(--color-dark)', // Cambia el color del ícono
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '0.7rem', // Tamaño del texto dentro del campo de entrada
                                                    padding: '0.8rem 12px', // Ajuste de padding para alinear correctamente el texto
                                                }
                                            }}
                                        >
                                            <MenuItem value="Ninguno" style={{ fontSize: '0.7rem' }}>Ninguno</MenuItem>
                                            {devices.sort(mixedNaturalSort).map((device, deviceIndex) => (
                                                <MenuItem key={deviceIndex} value={device.label} style={{ fontSize: '0.7rem' }}>
                                                    {device.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        fullWidth
                                        label="Hora de inicio"
                                        variant="outlined"
                                        value={schedule.startTime}
                                        disabled={false}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                                                },
                                                '&:not(.Mui-focused):hover fieldset': {
                                                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.8rem', // Tamaño del texto dentro del campo de entrada
                                                padding: '1rem 12px', // Ajuste de padding para alinear correctamente el texto
                                                color: 'var(--color-dark)', // Color del texto
                                            }
                                        }}
                                    />
                                    <FormControl fullWidth >
                                        <InputLabel id="multiSchedule-control-point-start-label" sx={{
                                            color: 'var(--color-dark)',
                                            fontSize: '0.7rem', // Tamaño de la etiqueta cuando NO está en foco
                                            '&.MuiInputLabel-shrink': {
                                                fontSize: '1rem', // Mantiene el mismo tamaño cuando está en foco
                                            }
                                        }}>Punto de Control Inicial</InputLabel>
                                        <Select
                                            labelId="multiSchedule-control-point-start-label"
                                            value={schedule.cpStart || controlPointStart}
                                            onChange={(e) => handleMultiScheduleChange(index, "cpStart", e.target.value)}
                                            label="Punto de Control Inicial"
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                                                },
                                                '&:not(.Mui-focused):hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                                                },
                                                '& .MuiSelect-select': {
                                                    color: 'var(--color-dark)',
                                                    fontSize: '0.7rem', // Tamaño del texto dentro del `Select`
                                                    padding: '0.8rem 12px'
                                                }
                                            }}
                                        >
                                            {routeOrder.map((geofence, index) => (
                                                <MenuItem key={index} value={geofence} style={{ fontSize: '0.7rem' }}>
                                                    {geofence}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth >
                                        <InputLabel id="multiSchedule-control-point-end-label" sx={{
                                            color: 'var(--color-dark)',
                                            fontSize: '0.7rem', // Tamaño de la etiqueta cuando NO está en foco
                                            '&.MuiInputLabel-shrink': {
                                                fontSize: '1rem', // Mantiene el mismo tamaño cuando está en foco
                                            }
                                        }}>Punto de Control Final</InputLabel>
                                        <Select
                                            labelId="multiSchedule-control-point-end-label"
                                            value={schedule.cpEnd || controlPointEnd}
                                            onChange={(e) => handleMultiScheduleChange(index, "cpEnd", e.target.value)}
                                            label="Punto de Control Inicial"
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                                                },
                                                '&:not(.Mui-focused):hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                                                },
                                                '& .MuiSelect-select': {
                                                    color: 'var(--color-dark)',
                                                    fontSize: '0.7rem', // Tamaño del texto dentro del `Select`
                                                    padding: '0.8rem 12px'
                                                }
                                            }}
                                        >
                                            {routeOrder.map((geofence, index) => (
                                                <MenuItem key={index} value={geofence} style={{ fontSize: '0.7rem' }}>
                                                    {geofence}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            ))}
                        </div>
                    </div>
                    <Box className="button_container">
                        <Button className="cancel_button" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button className="accept_button" onClick={handleAcceptMultiSchedule}>
                            Aceptar
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    )
}

export default MultiSchedulesModal